/**
 * Validation of CLIENT-supplied quiz drawing maps and handwritten answers
 * (questionId -> value). Pure and dependency-free so BOTH sides can run it: the
 * server on submit (netlify/functions/shared/drawingSanitize.ts wraps this with the
 * deployment's bucket list) and the client on the teacher's draft-rescue path,
 * which builds an attempt of record without ever touching the submit endpoint.
 *
 * These values are rendered by the teacher as <img src>, printed onto the marked
 * record, and (for handwritten answers) FETCHED server-side by the AI grader, so
 * accept ONLY Firebase Storage download URLs in a bucket WE own that resolve to
 * THIS class's own quiz-drawings prefix.
 *
 * `buckets` is an explicit parameter, never read from the environment here — that
 * is what keeps this module pure and identical on both sides.
 */

/**
 * Every bucket alias this project has used, across environments. Pinning the
 * bucket is load-bearing: without it a student can point their "answer" at an
 * object in their OWN Firebase project, which they can rewrite after submitting —
 * swap a placeholder for a worked solution once the test closes and be marked on
 * it. The list is deliberately generous, because a wrong pin silently drops real
 * student work, which is as damaging as accepting a forged URL.
 */
export const KNOWN_STORAGE_BUCKETS: readonly string[] = [
  'aero-app-4bb06-storage',
  'aero-app-4bb06.firebasestorage.app',
  'aero-app-4bb06.appspot.com',
  'levelupclassroom-staging.appspot.com',
  'levelupclassroom-staging.firebasestorage.app',
];

const STORAGE_HOST = 'firebasestorage.googleapis.com';

/**
 * True for a Firebase Storage download URL, in a bucket we own, whose OBJECT PATH
 * is under this class's quiz-drawings prefix.
 *
 * Parsed, never substring-matched. A substring check on the whole URL is trivially
 * padded — `?x=quiz-drawings%2F{classId}%2F` or `#quiz-drawings%2F{classId}%2F`
 * appended to any object in any bucket satisfies it — so the host, the bucket, and
 * the prefix are each checked against parsed components only.
 */
export function isClassDrawingUrl(
  val: string,
  classId: string,
  buckets: readonly string[] = KNOWN_STORAGE_BUCKETS,
): boolean {
  if (!classId || typeof val !== 'string' || !val || val.length > 1500) return false;
  let url: URL;
  try {
    url = new URL(val);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.hostname !== STORAGE_HOST) return false;
  // Download URLs are /v0/b/{bucket}/o/{percent-encoded object path}. `pathname`
  // excludes ?query and #fragment, which is what kills the padding bypass.
  return buckets.some(b => url.pathname.startsWith(`/v0/b/${b}/o/quiz-drawings%2F${classId}%2F`));
}

/**
 * Bounded, class-pinned validation of the `drawings` (url) / `drawingPaths` (path)
 * maps. Entry count + value length are capped so a tampered client can't bloat the
 * class doc toward the 1 MB limit. Returns undefined when nothing valid remains.
 */
export function sanitizeDrawingMap(
  v: unknown,
  mode: 'url' | 'path',
  classId: string,
  buckets: readonly string[] = KNOWN_STORAGE_BUCKETS,
): Record<string, string> | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v) || !classId) return undefined;
  const out: Record<string, string> = {};
  let n = 0;
  const pathPrefix = `quiz-drawings/${classId}/`;
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (n >= 200) break;
    if (typeof k !== 'string' || k.length > 120) continue;
    if (typeof val !== 'string' || !val || val.length > 1500) continue;
    if (mode === 'url' && !isClassDrawingUrl(val, classId, buckets)) continue;
    if (mode === 'path' && !val.startsWith(pathPrefix)) continue;
    out[k] = val;
    n++;
  }
  return n > 0 ? out : undefined;
}

/**
 * A `handwritten` answer is an image URL the student's own browser supplies, and the
 * teacher's marking screen renders it as <img src> (and hands it to the AI grader).
 * An unvalidated value there is stored XSS / a pointer at someone else's work / a
 * pointer at bytes the student can rewrite after the test, so every handwritten
 * answer is re-checked against the class-pinned Storage rule and DROPPED if it
 * fails — dropped, not blanked, so the question grades as unanswered (and still
 * forces manual review) rather than as a broken image a teacher might mark on trust.
 *
 * Every other answer type is left exactly as submitted (they are scored, not
 * rendered as markup). Must run on EVERY path that turns client answers into an
 * attempt of record: the normal submit AND the teacher's draft-rescue.
 */
export function sanitizeHandwrittenAnswers<T>(
  questions: Array<{ id: string; type?: string }>,
  answers: Record<string, T>,
  classId: string,
  buckets: readonly string[] = KNOWN_STORAGE_BUCKETS,
): Record<string, T> {
  const handwritten = questions.filter(q => q.type === 'handwritten').map(q => q.id);
  if (handwritten.length === 0) return answers;
  let changed = false;
  const out: Record<string, T> = { ...answers };
  for (const qid of handwritten) {
    const val = out[qid];
    if (val === undefined) continue;
    const trimmed = typeof val === 'string' ? val.trim() : '';
    if (!trimmed || !isClassDrawingUrl(trimmed, classId, buckets)) {
      delete out[qid];
      changed = true;
    } else if (trimmed !== val) {
      // Store what was validated, never the untrimmed original.
      out[qid] = trimmed as unknown as T;
      changed = true;
    }
  }
  return changed ? out : answers;
}

/**
 * The Storage OBJECT PATH inside a Firebase download URL, or null.
 *
 * Download URLs look like
 *   https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{percent-encoded path}?alt=media&token=…
 * so the path is the `/o/` segment, percent-decoded (`%2F` back to `/`).
 *
 * Used by the nightly orphan sweep to work out which stored objects are still
 * referenced by an attempt. Getting this wrong DELETES STUDENT WORK — a path that
 * fails to resolve looks unreferenced — which is why it lives here, pure and
 * tested, rather than inline in the cleanup job.
 */
export function storagePathFromDownloadUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  const marker = '/o/';
  const at = value.indexOf(marker);
  if (at === -1) return null;
  const encoded = value.slice(at + marker.length).split('?')[0].split('#')[0];
  if (!encoded) return null;
  try {
    const decoded = decodeURIComponent(encoded);
    return decoded || null;
  } catch {
    return null; // malformed escape — treat as unresolvable, never as unreferenced
  }
}

/**
 * Every quiz-drawing storage path referenced anywhere inside an arbitrary document,
 * found by walking its JSON rather than by knowing its shape.
 *
 * The nightly orphan sweep deletes drawings nothing references, so MISSING a
 * reference destroys a student's submitted work. Enumerating the known homes
 * (attempts, drafts, deletion records…) means the sweep silently breaks the day
 * anyone stores a drawing URL somewhere new — so this deliberately trades precision
 * for safety: it over-collects, and over-retaining an object costs pennies while
 * under-collecting costs a student their answer.
 *
 * Picks up both raw paths (`quiz-drawings/...`, as stored in drawingPaths) and the
 * percent-encoded form inside download URLs (`quiz-drawings%2F...`).
 */
export function collectDrawingPaths(value: unknown, into: Set<string> = new Set()): Set<string> {
  let text: string;
  try {
    text = JSON.stringify(value) ?? '';
  } catch (err) {
    // "Couldn't read it" must never be mistaken for "references nothing" — that is
    // a delete signal to the caller. Fail loudly so the sweep aborts instead.
    throw new Error(`collectDrawingPaths: unserializable document (${err instanceof Error ? err.message : String(err)})`);
  }
  if (!text) return into;

  // Encoded form, inside download URLs. Stop at the quote/query that ends the path.
  for (const m of text.matchAll(/quiz-drawings%2F[^"'?\\\s]+/gi)) {
    try {
      const decoded = decodeURIComponent(m[0]);
      if (decoded) into.add(decoded);
    } catch { /* malformed escape — skip this candidate, keep the raw one below */ }
  }
  // Raw form, as stored in drawingPaths.
  for (const m of text.matchAll(/quiz-drawings\/[^"'?\\\s]+/g)) {
    into.add(m[0]);
  }
  return into;
}
